/**
 * 진짜 micro:bit 보드(PICNIC-MB-FIELD-001 v0.2 §6 4단계) — USB 로 꽂은 보드를 Web Serial 로 연다.
 *
 * 보드 프로그램의 '놀이터에 연결' 블록(Bench contracts/makecode/picnic-board.ts)이 0.1초마다 시뮬레이터 때와 같은 한 줄(`PF1 L=… A=… B=… G=0 S=… X=… Y=…`)을
 * 115200 bps 로 보낸다 — 줄 단위로 끊어 lib/live parseFrame 으로 읽는다(시뮬레이터 소식과 같은 판으로 장면이 움직인다).
 * 놀이터가 보드로 보내는 것은 AI 값 한 줄(`AI:클래스:확신도\n` — 피크닉 AI 확장이 시리얼로 받는다)뿐이다.
 * Web Serial 은 크롬 · 엣지(데스크톱 · 크롬북)에서 된다 — 없으면 단추를 두지 않는다.
 */
export const BOARD_BAUD = 115_200;
/** micro:bit USB(DAPLink) 제조사 id — 포트 고르기 창에 micro:bit 만 보이게 */
export const MICROBIT_VENDOR = 0x0d28;

/** 이 브라우저가 Web Serial 을 쓸 수 있나 */
export const isBoardSupported = (nav: unknown = typeof navigator !== "undefined" ? navigator : undefined): boolean =>
  !!nav && typeof (nav as { serial?: { requestPort?: unknown } }).serial?.requestPort === "function";

/** 열린 보드 한 개 */
export interface BoardLink {
  write(line: string): Promise<void>;
  close(): Promise<void>;
}

/** 들어오는 바이트를 줄로 — 끝이 없는 조각은 남겨 두었다가 다음 조각과 잇는다(줄 하나는 200자를 넘지 않는다 — 넘으면 버린다) */
export class LineSplitter {
  private buf = "";
  push(chunk: string): string[] {
    this.buf += chunk;
    const parts = this.buf.split(/\r?\n/);
    this.buf = parts.pop() ?? "";
    if (this.buf.length > 400) this.buf = "";
    return parts.map((l) => l.trim()).filter((l) => l.length > 0 && l.length <= 200);
  }
}

type SerialPortLike = {
  open(o: { baudRate: number }): Promise<void>;
  close(): Promise<void>;
  readable: ReadableStream<Uint8Array> | null;
  writable: WritableStream<Uint8Array> | null;
};

/**
 * 포트를 골라 연다(사용자 몸짓 안에서 불러야 한다 — 고르기 창). 줄이 오면 onLine, 끊기면 onClose.
 * @throws 고르기를 취소했거나 열지 못하면
 */
export async function openBoard(onLine: (line: string) => void, onClose: () => void, nav: unknown = navigator): Promise<BoardLink> {
  const serial = (nav as { serial: { requestPort(o: unknown): Promise<SerialPortLike> } }).serial;
  const port = await serial.requestPort({ filters: [{ usbVendorId: MICROBIT_VENDOR }] });
  await port.open({ baudRate: BOARD_BAUD });
  const reader = port.readable?.getReader() ?? null;
  const writer = port.writable?.getWriter() ?? null;
  const dec = new TextDecoder();
  const enc = new TextEncoder();
  const split = new LineSplitter();
  let closed = false;
  void (async () => {
    try {
      while (reader && !closed) {
        const { value, done } = await reader.read();
        if (done) break;
        if (value) for (const l of split.push(dec.decode(value, { stream: true }))) onLine(l);
      }
    } catch { /* 뽑혔다 */ }
    if (!closed) { closed = true; onClose(); }
  })();
  return {
    async write(line) {
      if (closed || !writer) return;
      try { await writer.write(enc.encode(line.endsWith("\n") ? line : line + "\n")); } catch { /* 뽑혔다 — 읽기 쪽이 onClose */ }
    },
    async close() {
      if (closed) return;
      closed = true;
      try { await reader?.cancel(); } catch { /* 이미 닫힘 */ }
      try { reader?.releaseLock(); writer?.releaseLock(); } catch { /* */ }
      try { await port.close(); } catch { /* */ }
      onClose();
    },
  };
}
